import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enrichedHotels, HOTEL_TYPES, romaniaHotels, BOOKING_LINK, EnrichedHotel } from '@/data/romaniaHub';
import { romaniaRegions } from '@/data/hotels.romania';
import { MapPin, Star, ArrowRight, ChevronDown } from 'lucide-react';

const LuxuryRating: React.FC<{ level: number }> = ({ level }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${i < level ? 'fill-[#C9A961] text-[#C9A961]' : 'text-[#E8DFD0]'}`}
      />
    ))}
  </div>
);

const HotelCard: React.FC<{ hotel: EnrichedHotel; onView: () => void }> = ({ hotel, onView }) => (
  <article className="group bg-white border border-[#E8DFD0] hover:border-[#C9A961]/40 hover:shadow-xl transition-all duration-500 overflow-hidden flex flex-col">
    <div className="relative aspect-[3/2] overflow-hidden">
      <img
        src={hotel.image}
        alt={hotel.alt}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute top-3 left-3 bg-[#1A3B5C]/90 text-white px-3 py-1 font-sans text-[9px] tracking-[0.15em] uppercase">
        {hotel.type}
      </div>
      <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 flex items-center gap-1.5">
        <MapPin className="w-3 h-3 text-[#C9A961]" />
        <span className="font-sans text-[10px] tracking-wider uppercase text-[#1A3B5C]">{hotel.location}</span>
      </div>
    </div>

    <div className="p-6 flex flex-col flex-1">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-serif text-lg text-[#1A3B5C] font-light leading-snug group-hover:text-[#C9A961] transition-colors">
          {hotel.name}
        </h3>
      </div>
      <LuxuryRating level={hotel.luxury} />
      <p className="font-sans text-xs text-[#666] leading-relaxed my-4 line-clamp-3">{hotel.description}</p>

      <div className="flex flex-wrap gap-1.5 mb-5">
        {hotel.amenities.slice(0, 4).map((a) => (
          <span key={a} className="font-sans text-[9px] tracking-wide uppercase text-[#8a7a55] bg-[#FAF3E4] px-2 py-1">
            {a}
          </span>
        ))}
      </div>

      <button
        onClick={onView}
        className="mt-auto font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] flex items-center gap-2 hover:gap-3 transition-all pt-4 border-t border-[#E8DFD0]"
      >
        View Details <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  </article>
);

const Dropdown: React.FC<{
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => (
  <div className="relative w-full sm:w-56">
    <label className="sr-only">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none bg-white border border-[#E8DFD0] focus:border-[#C9A961] focus:outline-none font-sans text-sm text-[#1A3B5C] px-5 py-3 pr-11 cursor-pointer"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
    <ChevronDown className="w-4 h-4 text-[#C9A961] absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
  </div>
);

const FeaturedHotels: React.FC = () => {
  const navigate = useNavigate();
  const [region, setRegion] = useState('All Regions');
  const [type, setType] = useState('All Types');
  const [lux, setLux] = useState('Any Luxury Level');

  const filtered = useMemo(() => {
    return enrichedHotels.filter((h) => {
      if (region !== 'All Regions' && h.region !== region) return false;
      if (type !== 'All Types' && h.type !== type) return false;
      if (lux === '5 Stars' && h.luxury < 5) return false;
      if (lux === '4 Stars & Up' && h.luxury < 4) return false;
      return true;
    });
  }, [region, type, lux]);

  return (
    <section id="hotels" className="py-24 lg:py-32 bg-[#F5F1E8]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              The Collection
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6">
            Featured Luxury Hotels
          </h2>
          <p className="font-sans text-sm text-[#666] max-w-2xl mx-auto leading-relaxed">
            Castle hotels, alpine lodges, spa resorts and noble estates — {romaniaHotels.length} curated
            stays across Romania, filterable to your taste.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center mb-12">
          <Dropdown label="Region" value={region} options={['All Regions', ...romaniaRegions]} onChange={setRegion} />
          <Dropdown label="Type" value={type} options={['All Types', ...HOTEL_TYPES]} onChange={setType} />
          <Dropdown label="Luxury" value={lux} options={['Any Luxury Level', '4 Stars & Up', '5 Stars']} onChange={setLux} />
        </div>

        <p className="text-center font-sans text-[10px] tracking-[0.25em] uppercase text-[#999] mb-10">
          {filtered.length} {filtered.length === 1 ? 'Property' : 'Properties'}
        </p>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((h) => (
              <HotelCard key={h.id} hotel={h} onView={() => navigate(BOOKING_LINK)} />
            ))}
          </div>
        ) : (
          <p className="text-center font-sans text-sm text-[#777] py-16">
            No properties match these filters — try broadening your selection.
          </p>
        )}
      </div>
    </section>
  );
};

export default FeaturedHotels;
